import { useState, useEffect } from 'react'
import { X, Download, FileText, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../Toast'
import { BulkDownloadGroup } from '../../types/personale'

interface BulkDownloadModalProps {
  isOpen: boolean
  onClose: () => void
  personaleIds: string[]
}

export function BulkDownloadModal({
  isOpen,
  onClose,
  personaleIds
}: BulkDownloadModalProps) {
  const [groups, setGroups] = useState<BulkDownloadGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedAllegati, setSelectedAllegati] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    if (isOpen && personaleIds.length > 0) {
      loadPreview()
    }
    if (!isOpen) {
      setGroups([])
      setSelectedAllegati(new Set())
      setExpandedGroups(new Set())
    }
  }, [isOpen, personaleIds])

  const loadPreview = async () => {
    try {
      setLoading(true)
      const res = await api.post('/v1/personale/bulk-download-preview', {
        personale_ids: personaleIds
      })
      const data: BulkDownloadGroup[] = res.data
      setGroups(data)

      // Seleziona tutti gli allegati e espandi tutti i gruppi
      const allIds = new Set<string>()
      const allGroups = new Set<string>()
      data.forEach(group => {
        allGroups.add(group.personale_id)
        group.allegati.forEach(a => allIds.add(a.id))
      })
      setSelectedAllegati(allIds)
      setExpandedGroups(allGroups)
    } catch {
      setToast({ message: 'Errore nel caricamento anteprima', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const toggleAllegato = (id: string) => {
    setSelectedAllegati(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleGroup = (group: BulkDownloadGroup) => {
    const groupAllegatoIds = group.allegati.map(a => a.id)
    const allSelected = groupAllegatoIds.every(id => selectedAllegati.has(id))

    setSelectedAllegati(prev => {
      const next = new Set(prev)
      if (allSelected) {
        groupAllegatoIds.forEach(id => next.delete(id))
      } else {
        groupAllegatoIds.forEach(id => next.add(id))
      }
      return next
    })
  }

  const toggleExpand = (personaleId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(personaleId)) {
        next.delete(personaleId)
      } else {
        next.add(personaleId)
      }
      return next
    })
  }

  const isGroupSelected = (group: BulkDownloadGroup) => {
    return group.allegati.length > 0 && group.allegati.every(a => selectedAllegati.has(a.id))
  }

  const isGroupPartial = (group: BulkDownloadGroup) => {
    const selected = group.allegati.filter(a => selectedAllegati.has(a.id))
    return selected.length > 0 && selected.length < group.allegati.length
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const handleDownload = async () => {
    if (selectedAllegati.size === 0) {
      setToast({ message: 'Seleziona almeno un documento', type: 'error' })
      return
    }

    try {
      setDownloading(true)
      const res = await api.post('/v1/personale/bulk-download', {
        allegato_ids: Array.from(selectedAllegati)
      }, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'documenti_personale.zip')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      setToast({ message: 'Download completato', type: 'success' })
      onClose()
    } catch {
      setToast({ message: 'Errore nel download', type: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const totalDocumenti = groups.reduce((sum, g) => sum + g.allegati.length, 0)
  const totalSelezionati = selectedAllegati.size

  if (!isOpen) return null

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Download className="w-5 h-5 text-primary-600" />
                Scarica Documenti
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {personaleIds.length} {personaleIds.length === 1 ? 'persona selezionata' : 'persone selezionate'}
                {' '} - {totalSelezionati}/{totalDocumenti} documenti
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Caricamento documenti...</div>
            ) : totalDocumenti === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun documento trovato per le persone selezionate</p>
              </div>
            ) : (
              <div className="space-y-2">
                {groups.map(group => (
                  <div key={group.personale_id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <div
                      className="flex items-center gap-3 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleExpand(group.personale_id)}
                    >
                      <input
                        type="checkbox"
                        checked={isGroupSelected(group)}
                        ref={(el) => {
                          if (el) el.indeterminate = isGroupPartial(group)
                        }}
                        onChange={(e) => {
                          e.stopPropagation()
                          toggleGroup(group)
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        disabled={group.allegati.length === 0}
                      />
                      {expandedGroups.has(group.personale_id) ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="font-medium text-gray-900 text-sm flex-1">
                        {group.nome_completo}
                      </span>
                      <span className="text-xs text-gray-500">
                        {group.allegati.length} {group.allegati.length === 1 ? 'documento' : 'documenti'}
                      </span>
                    </div>

                    {/* Allegati List */}
                    {expandedGroups.has(group.personale_id) && group.allegati.length > 0 && (
                      <div className="border-t border-gray-200">
                        {group.allegati.map(allegato => (
                          <label
                            key={allegato.id}
                            className="flex items-center gap-3 px-5 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedAllegati.has(allegato.id)}
                              onChange={() => toggleAllegato(allegato.id)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-gray-700 truncate block">
                                {allegato.titolo}
                              </span>
                              <span className="text-xs text-gray-400">
                                {allegato.filename} - {formatFileSize(allegato.file_size)}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Empty state for group */}
                    {expandedGroups.has(group.personale_id) && group.allegati.length === 0 && (
                      <div className="px-5 py-3 text-xs text-gray-400 border-t border-gray-200">
                        Nessun documento
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {totalDocumenti > 0 && (
            <div className="p-5 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    I documenti saranno organizzati in cartelle per persona nel file ZIP.
                  </p>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={downloading || selectedAllegati.size === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  {downloading ? 'Generazione...' : `Scarica ZIP (${totalSelezionati})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
